<template>
  <div>
    <d-row no-gutters class="page-header py-4 mb-2">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <template v-if="isInPopup">
          <h4>Lista badań</h4>
        </template>
        <template v-else>
          <h4>Lista badań pacjenta:
            <p class="mt-1 mb-0 patient-name">
              {{ patient.name + ' ' + patient.last_name }}
            </p>
          </h4>
        </template>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="showAddMedicalPopup"
          :disabled="!loaded">
          <i class="material-icons">add</i>
          Dodaj badanie
        </d-button>
      </d-col>
    </d-row>

    <d-row class="d-flex ml-0 mb-3 page-filters w-100">
      <validation-observer
        ref="medical-examinations-filters-form"
        tag="form"
        class="d-flex w-100">
        <d-input
          placeholder="Podaj nazwę badania"
          v-model="filters.search" />

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">Szukaj</d-button>
        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>

    <table class="table mt-0 mb-0 full-width">
      <thead class="bg-light">
        <tr>
          <th scope="col" class="border-0">Nazwa badania</th>
          <th scope="col" class="border-0">Wizyta</th>
          <th scope="col" class="border-0">Specjalista</th>
          <th scope="col" class="border-0">Notatka</th>
          <th scope="col" class="border-0 text-center">Skan badania</th>
          <th scope="col" class="border-0 text-center">Akcje</th>
        </tr>
      </thead>
      <tbody v-if="loaded">
        <tr
          v-for="(medical, indexMedical) of medicalExaminations"
          :key="'key-' + medical.id + '-' + indexMedical">
          <td class="align-middle">
            {{ medical.examination_name || 'brak' }}
          </td>
          <td class="align-middle">
            <template v-if="medical.visit">
              <p style="color: #17c671">{{ medical.visit.date_start.slice(0, 11) }}</p>
              <span style="margin-top: -30px; display: block; font-size: 0.9rem; opacity: 0.6">
                {{ medical.visit.date_start.slice(11, 16) }} - {{ medical.visit.date_end.slice(11, 16) }}
              </span>
            </template>
            <template v-else>
              <span style="opacity: 0.5">Brak</span>
            </template>
          </td>
          <td class="align-middle">
            {{ medical.user.name }}
          </td>
          <td class="align-middle">
            <template v-if="medical.short_description">
              {{ medical.short_description }}
            </template>
            <template v-else>
              <span style="opacity: 0.5">Brak</span>
            </template>
          </td>
          <td class="align-middle text-center">
            <template v-if="medical.scan_file">
              <d-button
                theme="info"
                class="mx-auto"
                outline
                :disabled="actionInProgress !== false"
                @click.prevent="downloadMedical(medical.id, medical.scan_file.filename)">
                <template v-if="actionInProgress !== medical.scan_file">
                  <i class="material-icons">cloud_download</i>
                  <span v-if="!isInPopup" class="ml-2">Pobierz</span>
                </template>
                <template v-else>
                  <div class="spinner-border spinner-border-sm" role="status"></div>
                </template>
              </d-button>
            </template>
            <template v-else>
              <span style="opacity: 0.5">Brak</span>
            </template>
          </td>
          <td
            class="align-middle text-center"
            style="width: 180px;">
            <d-button
              v-if="!isInPopup && !filterForVisit && medical.visit_id"
              outline
              theme="info"
              class="mr-2"
              @click.prevent="showVisitPopup(medical.visit_id)"
              title="Podgląd">
              <i class="material-icons">person_search</i>
            </d-button>
            <d-button
              theme="primary"
              @click.prevent="showEditMedicalPopup(medical)"
              class="mr-2"
              title="Edytuj">
              <i class="material-icons">edit</i>
            </d-button>
            <d-button
              theme="danger"
              :disabled="removeInProgress"
              @click.prevent="removeItem(medical.id)"
              title="Usuń">
              <i class="material-icons">delete</i>
            </d-button>
          </td>
        </tr>
        <tr v-if="medicalExaminations.length === 0">
          <td
            colspan="6"
            class="text-center">
            Brak badań do wyświetlenia&hellip;
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="!loaded" class="pb-3 pt-3 text-center">
      Trwa ładowanie danych&hellip;
    </div>

    <d-alert
      v-if="loadError"
      show
      theme="warning">
      Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
    </d-alert>

    <patients-form-medical-popup
      :is-visible="isShowMedicalPopup"
      :medical="editMedical"
      :all-institutions="allInstitutions"
      :on-close-popup="handleCloseMedicalPopup"
      :on-submit-callback="debouncedLoadFilteredData"
      :patient="patient"
      :visit-id="visitId"/>
  </div>
</template>

<script>
import Vue from 'vue';
import axios from 'axios';
import api from '@/api';
import ListUtils from '@/utils/ListUtils.js';
import FormUtils from '@/utils/FormUtils.js';
import debounce from 'lodash.debounce';
import PatientsFormMedicalPopup from '@/components/forms/patients/PatientsFormMedicalPopup';

export default {
  name: 'patients-form-medical',
  props: {
    filterForVisit: {
      default: false,
      type: Boolean
    },
    isAddMedicalEnabled: {
      default: true,
      type: Boolean
    },
    isInPopup: {
      default: false,
      type: Boolean
    },
    isVisible: {
      default: false,
      type: Boolean
    },
    patient: {
      required: true,
      type: Object
    },
    visitId: {
      defualt: 0,
      type: Number
    },
    allInstitutions: {
      required: true,
      type: Array
    },
  },
  components: {
    'patients-form-medical-popup': PatientsFormMedicalPopup
  },
  watch: {
    'isVisible': function (newValue) {
      if (newValue && !this.loaded) {
        this.loadItemsData();
      }
    }
  },
  data () {
    return {
      actionInProgress: false,
      editMedical: null,
      filters: {
        search: ''
      },
      isShowMedicalPopup: false,
      loaded: false,
      loadError: false,
      medicalExaminations: [],
      removeInProgress: false
    }
  },
  mounted () {
    if (this.isVisible) {
      this.loadItemsData();
    }
  },
  methods: {
    loadItemsData () {
      let where = {
        name: this.filters.search
      };

      if (this.filterForVisit) {
        where.visit_id = this.visitId;
      }
      ListUtils.loadItemsData(this, {
        endpoint: '/patients/medical-examinations/items/' + this.patient.id,
        listField: 'medicalExaminations',
        pagination: false,
        params: {
          where
        }
      });
    },
    debouncedLoadFilteredData: debounce(function () {
      Vue.set(this, 'loaded', false);
      Vue.set(this, 'loadError', false);
      this.loadItemsData();
    }, 250),
    validateFiltersForm () {
      FormUtils.validate(this.$refs['medical-examinations-filters-form'], this.debouncedLoadFilteredData);
    },
    clearFilters () {
      this.filters = {
        search: ''
      };
      this.debouncedLoadFilteredData();
    },
    removeItem (medicalID) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/patients/medical-examinations/delete/',
        id: medicalID,
        successTitle: 'Usunięto badanie',
        successText: 'Wybrane badanie zostało usunięte',
        confirmTitle: 'Czy na pewno chcesz usunąć to badanie?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie badania nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.debouncedLoadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    downloadMedical (medicalID, name) {
      this.actionInProgress = true;
      let filename = name;

      axios({
        url: `${api}patients/medical-examinations/scan/get-file/` + medicalID,
        method: 'POST',
        responseType: 'blob'
      }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.addEventListener('click', this.clickCallback, false);
        link.click();
        this.actionInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'Wystąpił błąd podczas pobierania',
          html: 'Spróbuj ponownie',
          type: 'warning',
          confirmButtonText: 'OK',
        });
        this.actionInProgress = false;
      });
    },
    clickCallback (e) {
      e.stopPropagation();
    },
    showEditMedicalPopup (medical) {
      Vue.set(this, 'isShowMedicalPopup', true);
      Vue.set(this, 'editMedical', medical);
    },
    showAddMedicalPopup () {
      Vue.set(this, 'isShowMedicalPopup', true);
      Vue.set(this, 'editMedical', null);
    },
    handleCloseMedicalPopup () {
      Vue.set(this, 'isShowMedicalPopup', false);
      Vue.set(this, 'editMedical', null);
    },
    showVisitPopup (visitID) {
      this.$bus.$emit('patient-visit-show-popup', visitID);
    },
  }
}
</script>
